'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Chapter } from '@/components/layout/Section';

export type StickyNavPillPosition = {
  /** Offset from the list’s inline-start edge (LTR: left; RTL: right). */
  offset: number;
  width: number;
  ready: boolean;
};

const INITIAL_PILL: StickyNavPillPosition = {
  offset: 0,
  width: 0,
  ready: false,
};

/**
 * Measures the active sticky-nav link and returns CSS-friendly pill geometry
 * so a single shared indicator can slide with transform + width transitions
 * (replaces Motion `layoutId`).
 */
export function useStickyNavPillPosition(activeChapter: Chapter): {
  listRef: React.RefObject<HTMLUListElement | null>;
  setLinkRef: (id: Chapter, node: HTMLAnchorElement | null) => void;
  pill: StickyNavPillPosition;
} {
  const listRef = useRef<HTMLUListElement | null>(null);
  const linkRefs = useRef(new Map<Chapter, HTMLAnchorElement>());
  const [pill, setPill] = useState<StickyNavPillPosition>(INITIAL_PILL);

  const setLinkRef = useCallback((id: Chapter, node: HTMLAnchorElement | null) => {
    if (node) {
      linkRefs.current.set(id, node);
    } else {
      linkRefs.current.delete(id);
    }
  }, []);

  const updatePill = useCallback(() => {
    const list = listRef.current;
    const link = linkRefs.current.get(activeChapter);
    if (!list || !link) return;

    const listRect = list.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const isRtl = getComputedStyle(list).direction === 'rtl';
    const offset = isRtl
      ? listRect.right - linkRect.right
      : linkRect.left - listRect.left;

    setPill((prev) => {
      if (prev.ready && prev.offset === offset && prev.width === linkRect.width) {
        return prev;
      }
      return { offset, width: linkRect.width, ready: true };
    });
  }, [activeChapter]);

  useEffect(() => {
    updatePill();

    const list = listRef.current;
    if (!list) return;

    const resizeObserver = new ResizeObserver(() => {
      updatePill();
    });
    resizeObserver.observe(list);
    for (const link of linkRefs.current.values()) {
      resizeObserver.observe(link);
    }

    window.addEventListener('resize', updatePill);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePill);
    };
  }, [updatePill]);

  return { listRef, setLinkRef, pill };
}
